import { useState, useMemo } from 'react';
import { Formik, Form } from 'formik';
import { Plus } from 'lucide-react';

import { useRegistries } from '@/react/portainer/registries/queries/useRegistries';
import { pluralize } from '@/react/common/string-utils';

import { FormControl } from '@@/form-components/FormControl';
import { PortainerSelect, Option } from '@@/form-components/PortainerSelect';
import { TextTip } from '@@/Tip/TextTip';
import { Button } from '@@/buttons';
import { FormSection } from '@@/form-components/FormSection';
import { FormActions } from '@@/form-components/FormActions';

import { NamespaceSelect } from '../rbac/NamespaceSelect';

import { KubernetesRegistryFormData } from './types';
import { KubernetesRegistryAccessDatatable } from './KubernetesRegistryAccessDatatable';

interface Props {
  initialValues: KubernetesRegistryFormData;
  onSubmit: (values: KubernetesRegistryFormData) => void | Promise<void>;
  isSubmitting?: boolean;
  mode: 'create' | 'edit';
  nameProvided: boolean;
  environmentGroupsProvided: boolean;
}

export function KubernetesRegistryPolicyForm({
  initialValues,
  onSubmit,
  isSubmitting = false,
  mode,
  nameProvided,
  environmentGroupsProvided,
}: Props) {
  const [tempSelectedRegistry, setTempSelectedRegistry] = useState<
    number | null
  >(null);
  const [tempSelectedNamespaces, setTempSelectedNamespaces] = useState<
    string[]
  >([]);

  const registriesQuery = useRegistries({
    select: (registries) => registries.filter((r) => r.Id !== 0),
  });

  const registryOptions: Option<number>[] = useMemo(() => {
    const registries = registriesQuery.data || [];
    return registries.map((r) => ({ label: r.Name, value: r.Id }));
  }, [registriesQuery.data]);

  const canAddAccess =
    tempSelectedRegistry !== null && tempSelectedNamespaces.length > 0;

  return (
    <Formik<KubernetesRegistryFormData>
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnMount
    >
      {({ values, setFieldValue, isValid }) => {
        return (
          <Form>
            <FormSection title="Add access">
              <FormControl label="Select registry" required>
                <PortainerSelect
                  value={tempSelectedRegistry}
                  onChange={(value) => setTempSelectedRegistry(value || 0)}
                  options={registryOptions}
                  placeholder="Select a registry"
                  bindToBody
                  data-cy="registry-select"
                  isLoading={registriesQuery.isLoading}
                  isClearable
                />
              </FormControl>

              <FormControl label="Namespaces" required>
                <p className="mb-2 text-xs text-muted">
                  Add the namespaces that should have access to the registry:
                </p>
                <NamespaceSelect
                  selectedNamespaces={tempSelectedNamespaces}
                  onChange={setTempSelectedNamespaces}
                  placeholder="e.g. default, production"
                  data-cy="registry-namespaces-input"
                  namespaces={getNamespacesFromAccessList()}
                />
              </FormControl>

              <div className="form-group">
                <div className="col-sm-12 flex gap-2">
                  <Button
                    icon={Plus}
                    color="primary"
                    size="small"
                    disabled={!canAddAccess}
                    onClick={handleAddAccess}
                    data-cy="add-registry-access-button"
                    className="!ml-0"
                  >
                    Add Access
                  </Button>
                </div>
              </div>
            </FormSection>

            {/* Access List Datatable */}
            <div className="-mx-4">
              <KubernetesRegistryAccessDatatable
                formData={{ accessRows: values.accessRows }}
                onChange={(key, value) => setFieldValue(key, value)}
              />
            </div>

            {/* Info message */}
            <div className="form-group">
              <div className="col-sm-12">
                <TextTip color="blue">
                  {(() => {
                    const count = values.accessRows?.length ?? 0;
                    if (count > 0) {
                      return `Registry access configured for ${count} ${pluralize(
                        count,
                        'registry',
                        'registries'
                      )}.`;
                    }
                    return mode === 'create'
                      ? 'Please add at least one registry to create access.'
                      : 'Please add at least one registry to update access.';
                  })()}
                </TextTip>
              </div>
            </div>

            <FormActions
              submitLabel={
                mode === 'create' ? 'Create policy' : 'Update policy'
              }
              loadingText={mode === 'create' ? 'Creating...' : 'Updating...'}
              isLoading={isSubmitting}
              isValid={
                isValid &&
                !!nameProvided &&
                !!environmentGroupsProvided &&
                (values.accessRows?.length ?? 0) > 0
              }
              data-cy="policy-form-actions"
            />
          </Form>
        );

        function handleAddAccess() {
          if (!canAddAccess) return;

          const cleanedNamespaces = tempSelectedNamespaces
            .map((ns) => ns.trim())
            .filter((ns) => ns !== '');

          const newAccessRows = [...(values.accessRows || [])];
          const existingIndex = newAccessRows.findIndex(
            (row) => row.registryId === tempSelectedRegistry
          );

          if (existingIndex !== -1) {
            newAccessRows[existingIndex] = {
              ...newAccessRows[existingIndex],
              namespaces: cleanedNamespaces,
            };
          } else {
            newAccessRows.push({
              registryId: tempSelectedRegistry,
              namespaces: cleanedNamespaces,
            });
          }

          setFieldValue('accessRows', newAccessRows);
          setTempSelectedRegistry(null);
          setTempSelectedNamespaces([]);
        }

        function getNamespacesFromAccessList(): string[] {
          return Array.from(
            new Set(
              (values.accessRows || []).flatMap((a) => a.namespaces || [])
            )
          );
        }
      }}
    </Formik>
  );
}
